import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState(""); // email actually resolved by backend
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!identifier) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/request-otp", { identifier });
      const resolvedEmail = r.data?.email || identifier;
      setEmail(resolvedEmail);
      toast.success("Code envoyé", { description: `Vérifiez votre boîte email : ${resolvedEmail}` });
      setStep("code");
    } catch (err) {
      const detail = err?.response?.data?.detail || String(err);
      if (err?.response?.status === 404) {
        toast.error("Surnom introuvable", {
          description: "Utilisez votre email complet, puis créez un surnom dans Réglages une fois connecté.",
        });
      } else {
        toast.error("Échec de l'envoi", { description: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { email, code });
      await login(r.data.token);
      toast.success("Connecté");
      nav("/");
    } catch (err) {
      toast.error("Code invalide", { description: err?.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app grid-noise text-app flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg border border-line-strong flex items-center justify-center">
            <span className="font-display font-bold text-invest">G</span>
          </div>
          <div>
            <div className="font-display text-2xl font-semibold tracking-tight text-app">GeniCap</div>
            <div className="text-xs text-muted-2 uppercase tracking-widest">journal de discipline</div>
          </div>
        </div>

        <div className="bg-card-1 border border-line rounded-xl p-8">
          {step === "email" ? (
            <form onSubmit={requestOtp} className="space-y-6">
              <div>
                <h1 className="font-display text-xl mb-1">Connexion</h1>
                <p className="text-sm text-muted-1">
                  Un code à 6 chiffres vous sera envoyé par email.
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-1 uppercase tracking-widest mb-2 block">
                  Email ou surnom
                </label>
                <Input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="vous@exemple.com ou votre surnom"
                  className="h-11 bg-input border-line-strong text-app"
                  data-testid="login-email-input"
                  required
                />
                <div className="mt-1.5 text-[11px] text-muted-2">
                  Si vous n'avez pas encore de surnom, connectez-vous avec l'email et créez-en un dans Réglages.
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !identifier}
                className="w-full h-11 btn-primary font-medium"
                data-testid="login-send-otp-btn"
              >
                {loading ? "Envoi..." : "Envoyer le code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-6">
              <div>
                <h1 className="font-display text-xl mb-1">Code reçu</h1>
                <p className="text-sm text-muted-1">
                  Saisissez le code envoyé à <span className="text-app font-mono-num">{email}</span>.
                </p>
              </div>
              <div className="flex justify-center">
                <InputOTP
                  value={code}
                  onChange={setCode}
                  maxLength={6}
                  data-testid="login-otp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg font-mono-num" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-11 btn-primary font-medium"
                data-testid="login-verify-btn"
              >
                {loading ? "Vérification..." : "Se connecter"}
              </Button>
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); setIdentifier(""); }}
                className="text-xs text-muted-1 hover:text-app transition-colors block mx-auto"
                data-testid="login-back-btn"
              >
                ← Changer d'identifiant
              </button>
            </form>
          )}
        </div>

        <p className="text-[11px] text-muted-2 mt-6 text-center leading-relaxed">
          Cet outil est un journal d'analyse quantitative. Il ne fournit aucun conseil en investissement,
          ne garantit aucun rendement et n'exécute aucun ordre.
        </p>
      </div>
    </div>
  );
}
